import { NoAttribution, OrgRole, ThemeType } from "@hex/common";
import React from "react";
import { Navigate } from "react-router";
import { styled } from "styled-components";

import { BackgroundGrid } from "../../components/homepage/HomeBackgrounds";
import { WarningIcon } from "../../components/icons/CustomIcons";
import {
  HexCard,
  HexNonIdealState,
  HexSpinner,
  HexSpinnerSizes,
} from "../../hex-components";
import { useMeQuery } from "../../route/HexRoutes.generated";
import { Routes } from "../../route/routes";

import { CliAuthFlow } from "./CliAuthFlow";
import { getCurrentUrl } from "./util";

export const CliAuthView = React.memo(function CliAuthView() {
  return (
    <Layout>
      <AuthCard>
        <EnsureLoggedIn>
          <CliAuthFlow />
        </EnsureLoggedIn>
      </AuthCard>
      <BackgroundGrid css="z-index: -1;" />
    </Layout>
  );
});

const Layout = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  z-index: 1;
`;

const AuthCard = styled(HexCard)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: hidden;

  width: 520px;
  padding: 40px;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  border-radius: 6px;
  box-shadow: none;
  background-color: ${({ theme }) =>
    theme.type === ThemeType.DARK
      ? theme.backgroundColor.MUTED
      : theme.backgroundColor.DEFAULT};

  @media (max-width: 600px) {
    width: 100%;
    padding: 20px;
    height: 100%;
    overflow: auto;
  }
`;

const EnsureLoggedIn = React.memo(function EnsureLoggedIn({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: meData, error, loading } = useMeQuery();

  const loggedOut =
    !loading &&
    (!meData?.me || error || meData.me.orgRole === OrgRole.ANONYMOUS);
  if (loggedOut) {
    // deflect to login
    const redirectBackHere = getCurrentUrl();
    return (
      <Navigate
        replace={true}
        to={Routes.LOGIN.getUrl({
          attribution: NoAttribution,
          queryParams: { redirectTo: redirectBackHere },
        })}
      />
    );
  }

  if (!meData && loading) {
    return <HexSpinner size={HexSpinnerSizes.LARGE} />;
  } else if (!meData || error || !meData.me || !meData.myOrg) {
    return (
      <HexNonIdealState
        description="There was an unexpected error. Please contact support@hex.tech for assistance."
        icon={<WarningIcon />}
        title="Something went wrong"
      />
    );
  } else {
    // logged in
    return children;
  }
});
